import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { formatDate } from "../../lib/appUtils";
import { GlobalNav } from "../shared/GlobalNav";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Users, Plus, Mail, Edit3, Trash2, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditClientModal = ({ client, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_name: client?.name || '',
    client_email: client?.email || '',
    client_company: client?.company || '',
    client_billing_address: client?.billing_address || '',
    client_billing_city: client?.billing_city || '',
    client_billing_postcode: client?.billing_postcode || '',
    client_billing_country: client?.billing_country || 'United Kingdom'
  });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (client) {
      setFormData({
        client_name: client.name,
        client_email: client.email,
        client_company: client.company || '',
        client_billing_address: client.billing_address || '',
        client_billing_city: client.billing_city || '',
        client_billing_postcode: client.billing_postcode || '',
        client_billing_country: client.billing_country || 'United Kingdom'
      });
    }
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/clients/${encodeURIComponent(client.email)}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Client updated",
        description: "Client information has been updated successfully"
      });
      
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update client",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Client</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_client_name" className="font-body">Client Name</Label>
              <Input
                id="edit_client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="font-body"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_client_email" className="font-body">Client Email</Label>
              <Input
                id="edit_client_email"
                type="email"
                value={formData.client_email}
                className="font-body bg-gray-50"
                readOnly
                title="Email cannot be changed as it's used as the client identifier"
              />
              <p className="text-xs text-gray-500 mt-1 font-body">
                Email cannot be changed as it's the client identifier
              </p>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="edit_client_company" className="font-body">Client Company</Label>
              <Input
                id="edit_client_company"
                value={formData.client_company}
                onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                placeholder="Company Ltd"
                className="font-body"
              />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-4 font-heading">Billing Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="edit_billing_address" className="font-body">Address</Label>
                <Input
                  id="edit_billing_address"
                  value={formData.client_billing_address}
                  onChange={(e) => setFormData({ ...formData, client_billing_address: e.target.value })}
                  placeholder="123 High Street"
                  className="font-body"
                />
              </div>
              
              <div>
                <Label htmlFor="edit_billing_city" className="font-body">City</Label>
                <Input
                  id="edit_billing_city"
                  value={formData.client_billing_city}
                  onChange={(e) => setFormData({ ...formData, client_billing_city: e.target.value })}
                  placeholder="London"
                  className="font-body"
                />
              </div>
              
              <div>
                <Label htmlFor="edit_billing_postcode" className="font-body">Postcode</Label>
                <Input
                  id="edit_billing_postcode"
                  value={formData.client_billing_postcode}
                  onChange={(e) => setFormData({ ...formData, client_billing_postcode: e.target.value })}
                  placeholder="SW1A 1AA"
                  className="font-body"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="edit_billing_country" className="font-body">Country</Label>
                <Select 
                  value={formData.client_billing_country} 
                  onValueChange={(value) => setFormData({ ...formData, client_billing_country: value })}
                >
                  <SelectTrigger className="font-body">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Ireland">Ireland</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2 font-heading">Project History</h4>
            <div className="text-sm text-gray-600 font-body">
              <div className="flex justify-between mb-1">
                <span>Total Projects:</span>
                <span>{client.project_count}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Total Value:</span>
                <span>£{client.total_value.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Last Project:</span>
                <span>{formatDate(client.last_project_date)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="font-body"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-black hover:bg-gray-800 text-white font-body"
            >
              {loading ? "Updating..." : "Update Client"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch clients",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowEditModal(true);
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    
    try {
      toast({
        title: "Client Deleted",
        description: `${clientToDelete.name} has been removed from your client list`
      });
      
      setClients(clients.filter(c => c.email !== clientToDelete.email));
      
      setShowDeleteDialog(false);
      setClientToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete client",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="clients" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading clients...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="clients" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Clients</h1>
              <p className="text-gray-600 font-body">Manage your client relationships and project history.</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 font-heading">{clients.length}</div>
              <div className="text-sm text-gray-600 font-body">Total clients</div>
            </div>
          </div>
        </div>

        {clients.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-300 text-center py-12">
            <CardContent>
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No clients yet</h3>
              <p className="text-gray-600 mb-6 font-body">
                Clients will appear here when you create projects for them.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-black hover:bg-gray-800 text-white font-body"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {clients.map((client) => (
              <Card 
                key={client.email} 
                className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleEditClient(client)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 font-heading">{client.name}</h3>
                        <Badge variant="secondary" className="font-body">
                          {client.project_count} project{client.project_count !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3 font-body">
                        <div>
                          <div className="flex items-center space-x-1 mb-1">
                            <Users className="w-4 h-4" />
                            <span>{client.email}</span>
                          </div>
                          {client.company && client.company !== '-' && (
                            <div className="ml-5">{client.company}</div>
                          )}
                        </div>
                        
                        {client.billing_address && (
                          <div>
                            <div className="flex items-center space-x-1 mb-1">
                              <span className="font-medium">Billing:</span>
                            </div>
                            <div className="ml-2 text-xs">
                              <div>{client.billing_address}</div>
                              <div>
                                {client.billing_city && `${client.billing_city}, `}
                                {client.billing_postcode}
                              </div>
                              <div>{client.billing_country}</div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3 font-body">
                        <div>
                          £{client.total_value.toLocaleString()} total value
                        </div>
                        <div>
                          Last project: {formatDate(client.last_project_date)}
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2 font-body">Recent Projects</p>
                        <div className="flex flex-wrap gap-2">
                          {client.linked_projects.slice(0, 3).map((project) => (
                            <Badge 
                              key={project.id}
                              variant="outline" 
                              className="cursor-pointer hover:bg-gray-100 font-body"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${project.id}`);
                              }}
                            >
                              {project.title}
                              <span className={`ml-1 ${
                                project.status === 'archived' ? 'text-gray-500' :
                                project.status === 'active' ? 'text-blue-500' : 'text-gray-600'
                              }`}>
                                (£{project.total_amount.toLocaleString()})
                              </span>
                            </Badge>
                          ))}
                          {client.linked_projects.length > 3 && (
                            <Badge variant="secondary" className="font-body">
                              +{client.linked_projects.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`mailto:${client.email}`, '_blank');
                        }}
                        className="font-body"
                        title="Send Email to Client"
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClient(client);
                        }}
                        className="font-body"
                        title="Edit Client Details"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setClientToDelete(client);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600 hover:text-red-900 font-body"
                        title="Delete Client"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {showEditModal && editingClient && (
          <EditClientModal
            client={editingClient}
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingClient(null);
            }}
            onSuccess={() => {
              fetchClients();
              setShowEditModal(false);
              setEditingClient(null);
            }}
          />
        )}

        {showDeleteDialog && clientToDelete && (
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Delete Client</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-gray-700 font-body">
                  Are you sure you want to delete <strong>{clientToDelete.name}</strong> from your client list?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Info className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-800 font-body">
                      This will not affect any existing projects with this client.
                    </span>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setClientToDelete(null);
                    }}
                    className="font-body"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeleteClient}
                    className="bg-red-600 hover:bg-red-700 text-white font-body"
                  >
                    Delete Client
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
};

export default ClientsPage;
